import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Snackbar,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { QRCodeSVG } from 'qrcode.react';
import { io, Socket } from 'socket.io-client';
import api from '../../services/api';
import { API_URL, SOCKET_URL } from '../../config/api';
import { useI18n } from '../../i18n/useI18n';
import StaffUnlimitedQrCard from './StaffUnlimitedQrCard';

type PhotoboothSimpleModeProps = {
  /** Admin: quản lý máy của user này. Owner user tab: bỏ trống. */
  userId?: string;
  /** Mã máy — bắt buộc (setting theo từng máy) */
  machineCode: string;
};

const IDLE_TIMEOUT_MIN = 1;
const IDLE_TIMEOUT_MAX = 10;
const CAPTURE_COUNTDOWN_MIN = 1;
const CAPTURE_COUNTDOWN_MAX = 30;

const clampIdleMinutes = (value: unknown, fallback = 1) => {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(IDLE_TIMEOUT_MIN, Math.min(IDLE_TIMEOUT_MAX, n));
};

const clampCaptureCountdown = (value: unknown, fallback = 5) => {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(CAPTURE_COUNTDOWN_MIN, Math.min(CAPTURE_COUNTDOWN_MAX, n));
};

const PhotoboothSimpleMode: React.FC<PhotoboothSimpleModeProps> = ({ userId, machineCode }) => {
  const { pages } = useI18n();
  const t = pages.photoboothSimple;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [maxAccessPerDay, setMaxAccessPerDay] = useState(1);
  const [maxAccessInput, setMaxAccessInput] = useState('1');
  const [savingMax, setSavingMax] = useState(false);
  const [captureIdleMinutes, setCaptureIdleMinutes] = useState(1);
  const [captureIdleInput, setCaptureIdleInput] = useState('1');
  const [savingCaptureIdle, setSavingCaptureIdle] = useState(false);
  const [previewIdleMinutes, setPreviewIdleMinutes] = useState(1);
  const [previewIdleInput, setPreviewIdleInput] = useState('1');
  const [savingPreviewIdle, setSavingPreviewIdle] = useState(false);
  const [retakeEnabled, setRetakeEnabled] = useState(true);
  const [savingRetake, setSavingRetake] = useState(false);
  const [captureCountdownSeconds, setCaptureCountdownSeconds] = useState(5);
  const [captureCountdownInput, setCaptureCountdownInput] = useState('5');
  const [countdownFromMachine, setCountdownFromMachine] = useState(false);
  const [machineSocketOnline, setMachineSocketOnline] = useState(false);
  const [accessCode, setAccessCode] = useState<string | null>(null);
  const [accessCodeUpdatedAt, setAccessCodeUpdatedAt] = useState<string | null>(null);
  const [isClaimed, setIsClaimed] = useState(false);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const maxAccessDirtyRef = useRef(false);
  const captureIdleDirtyRef = useRef(false);
  const previewIdleDirtyRef = useRef(false);
  const captureCountdownDirtyRef = useRef(false);
  const socketRef = useRef<Socket | null>(null);

  const mc = (machineCode || '').trim().toUpperCase();
  const backendOrigin = API_URL.replace(/\/api\/?$/i, '');
  const publicUrl = `${backendOrigin}/photobooth-simple/${mc}`;

  const modeEndpoint = useMemo(() => {
    if (userId) return `/admin/users/${userId}/machines/${mc}/photobooth-simple-mode`;
    return `/machines/${mc}/photobooth-simple-mode`;
  }, [userId, mc]);

  const accessEndpoint = useMemo(() => {
    if (userId) return `/admin/users/${userId}/machines/${mc}/photobooth-simple-access`;
    return `/machines/${mc}/photobooth-simple-access`;
  }, [userId, mc]);

  const applyIdleFromPayload = useCallback((data: any) => {
    if (data?.photoboothSimpleCaptureIdleTimeoutMinutes != null) {
      const mins = clampIdleMinutes(data.photoboothSimpleCaptureIdleTimeoutMinutes);
      setCaptureIdleMinutes(mins);
      if (!captureIdleDirtyRef.current) setCaptureIdleInput(String(mins));
    }
    if (data?.photoboothSimplePreviewIdleTimeoutMinutes != null) {
      const mins = clampIdleMinutes(data.photoboothSimplePreviewIdleTimeoutMinutes);
      setPreviewIdleMinutes(mins);
      if (!previewIdleDirtyRef.current) setPreviewIdleInput(String(mins));
    }
  }, []);

  const applyRetakeFromPayload = useCallback((data: any) => {
    if (typeof data?.photoboothSimpleRetakeEnabled === 'boolean') {
      setRetakeEnabled(data.photoboothSimpleRetakeEnabled);
    } else if (typeof data?.retakeEnabled === 'boolean') {
      setRetakeEnabled(data.retakeEnabled);
    }
  }, []);

  const applyCountdownFromMachine = useCallback((secsRaw: unknown) => {
    if (secsRaw == null) return;
    const secs = clampCaptureCountdown(secsRaw);
    setCaptureCountdownSeconds(secs);
    if (!captureCountdownDirtyRef.current) setCaptureCountdownInput(String(secs));
    setCountdownFromMachine(true);
  }, []);

  /** Load lần đầu / sau Save — không poll; mã/claim cập nhật bằng socket. */
  const load = useCallback(async () => {
    if (!mc) return;
    setLoading(true);
    try {
      const res = await api.get(modeEndpoint);
      setEnabled(!!res.data?.photoboothSimpleMode);
      setUpdatedAt(res.data?.updatedAt || null);
      const max = res.data?.photoboothSimpleMaxDeviceAccessPerDay == null
        ? 1
        : Math.max(0, Number(res.data.photoboothSimpleMaxDeviceAccessPerDay) || 0);
      setMaxAccessPerDay(max);
      if (!maxAccessDirtyRef.current) setMaxAccessInput(String(max));
      applyIdleFromPayload(res.data);
      applyRetakeFromPayload(res.data);
      setAccessCode(res.data?.accessCode || null);
      setAccessCodeUpdatedAt(res.data?.accessCodeUpdatedAt || null);
      setIsClaimed(!!res.data?.isClaimed);

      if (res.data?.photoboothSimpleMode) {
        try {
          const access = await api.get(accessEndpoint);
          setAccessCode(access.data?.accessCode || res.data?.accessCode || null);
          setAccessCodeUpdatedAt(access.data?.accessCodeUpdatedAt || null);
          setIsClaimed(!!access.data?.isClaimed);
        } catch {
          /* mode payload đã đủ */
        }
      }
    } catch (e: any) {
      setSnack({
        open: true,
        message: e?.response?.data?.message || t.loadError,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [modeEndpoint, accessEndpoint, mc, t.loadError, applyIdleFromPayload, applyRetakeFromPayload]);

  useEffect(() => {
    void load();
  }, [load]);

  // Giống Mobile Upload: join machine room, nhận mã/claim realtime — không setInterval.
  useEffect(() => {
    if (!mc) return;

    const socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      path: '/socket.io',
    });
    socketRef.current = socket;

    const join = () => {
      socket.emit('join-machine-room', mc);
      socket.emit('device-mgmt-request-camera-settings', {
        machineCode: mc,
      });
    };
    socket.on('connect', join);
    if (socket.connected) join();

    socket.on('photobooth:simple-access-updated', (data: {
      machineCode?: string;
      accessCode?: string | null;
      accessCodeUpdatedAt?: string | null;
      isClaimed?: boolean;
    }) => {
      const code = String(data?.machineCode || '').trim().toUpperCase();
      if (code && code !== mc) return;
      if (data?.accessCode) setAccessCode(String(data.accessCode).toUpperCase());
      if (data?.accessCodeUpdatedAt) setAccessCodeUpdatedAt(data.accessCodeUpdatedAt);
      if (typeof data?.isClaimed === 'boolean') setIsClaimed(data.isClaimed);
    });

    socket.on('photobooth:simple-session-claimed', (data: {
      machineCode?: string;
      isClaimed?: boolean;
    }) => {
      const code = String(data?.machineCode || '').trim().toUpperCase();
      if (code && code !== mc) return;
      if (typeof data?.isClaimed === 'boolean') setIsClaimed(data.isClaimed);
    });

    socket.on('photobooth:simple-mode-changed', (data: {
      machineCode?: string;
      photoboothSimpleMode?: boolean;
      photoboothSimpleModeUpdatedAt?: string | null;
      photoboothSimpleMaxDeviceAccessPerDay?: number;
      photoboothSimpleCaptureIdleTimeoutMinutes?: number;
      photoboothSimplePreviewIdleTimeoutMinutes?: number;
      photoboothSimpleRetakeEnabled?: boolean;
      retakeEnabled?: boolean;
    }) => {
      const code = String(data?.machineCode || '').trim().toUpperCase();
      if (code && code !== mc) return;
      if (typeof data?.photoboothSimpleMode === 'boolean') setEnabled(data.photoboothSimpleMode);
      if (data?.photoboothSimpleModeUpdatedAt) setUpdatedAt(data.photoboothSimpleModeUpdatedAt);
      if (data?.photoboothSimpleMaxDeviceAccessPerDay != null) {
        const max = Math.max(0, Number(data.photoboothSimpleMaxDeviceAccessPerDay) || 0);
        setMaxAccessPerDay(max);
        if (!maxAccessDirtyRef.current) setMaxAccessInput(String(max));
      }
      applyIdleFromPayload(data);
      applyRetakeFromPayload(data);
    });

    // Countdown local trên máy — giống Control Page (camera-settings-update).
    socket.on('camera-settings-update', (data: {
      machineCode?: string;
      countdownSeconds?: number;
    }) => {
      const code = String(data?.machineCode || '').trim().toUpperCase();
      if (code && code !== mc) return;
      setMachineSocketOnline(true);
      if (typeof data?.countdownSeconds === 'number') {
        applyCountdownFromMachine(data.countdownSeconds);
      }
    });

    socket.on('app-status-update', (data: {
      machineCode?: string;
      isOnline?: boolean;
      isAppRunning?: boolean;
    }) => {
      const code = String(data?.machineCode || '').trim().toUpperCase();
      if (code && code !== mc) return;
      if (typeof data?.isAppRunning === 'boolean') setMachineSocketOnline(data.isAppRunning);
      else if (typeof data?.isOnline === 'boolean') setMachineSocketOnline(data.isOnline);
    });

    return () => {
      try {
        socket.emit('leave-machine-room', mc);
      } catch {
        /* ignore */
      }
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [mc, applyIdleFromPayload, applyRetakeFromPayload, applyCountdownFromMachine]);

  const onToggle = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const prev = enabled;
    setEnabled(checked);
    setSaving(true);
    try {
      const res = await api.patch(modeEndpoint, {
        photoboothSimpleMode: checked,
        source: userId ? 'admin' : 'web',
      });
      setEnabled(!!res.data?.photoboothSimpleMode);
      setUpdatedAt(res.data?.updatedAt || new Date().toISOString());
      if (res.data?.photoboothSimpleMaxDeviceAccessPerDay != null) {
        const max = Math.max(0, Number(res.data.photoboothSimpleMaxDeviceAccessPerDay) || 0);
        setMaxAccessPerDay(max);
        if (!maxAccessDirtyRef.current) setMaxAccessInput(String(max));
      }
      applyIdleFromPayload(res.data);
      applyRetakeFromPayload(res.data);
      setAccessCode(res.data?.accessCode || null);
      setAccessCodeUpdatedAt(res.data?.accessCodeUpdatedAt || null);
      setSnack({
        open: true,
        message: checked ? t.enableSuccess : t.disableSuccess,
        severity: 'success',
      });
      if (checked) await load();
    } catch (e: any) {
      setEnabled(prev);
      setSnack({
        open: true,
        message: e?.response?.data?.message || t.saveError,
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const onToggleRetake = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const prev = retakeEnabled;
    setRetakeEnabled(checked);
    setSavingRetake(true);
    try {
      const res = await api.patch(modeEndpoint, {
        photoboothSimpleRetakeEnabled: checked,
        source: userId ? 'admin' : 'web',
      });
      applyRetakeFromPayload(res.data);
      setSnack({
        open: true,
        message: checked ? t.retakeEnableSuccess : t.retakeDisableSuccess,
        severity: 'success',
      });
    } catch (e: any) {
      setRetakeEnabled(prev);
      setSnack({
        open: true,
        message: e?.response?.data?.message || t.saveError,
        severity: 'error',
      });
    } finally {
      setSavingRetake(false);
    }
  };

  const applyMaxAccess = async (nextRaw: number) => {
    const parsed = Math.max(0, Math.min(9999, Math.floor(Number(nextRaw))));
    if (!Number.isFinite(parsed) || parsed === maxAccessPerDay) {
      setMaxAccessInput(String(maxAccessPerDay));
      return;
    }
    const prev = maxAccessPerDay;
    maxAccessDirtyRef.current = false;
    setMaxAccessPerDay(parsed);
    setMaxAccessInput(String(parsed));
    setSavingMax(true);
    try {
      const res = await api.patch(modeEndpoint, {
        photoboothSimpleMaxDeviceAccessPerDay: parsed,
        source: userId ? 'admin' : 'web',
      });
      const max = res.data?.photoboothSimpleMaxDeviceAccessPerDay == null
        ? parsed
        : Math.max(0, Number(res.data.photoboothSimpleMaxDeviceAccessPerDay) || 0);
      setMaxAccessPerDay(max);
      setMaxAccessInput(String(max));
    } catch (e: any) {
      setMaxAccessPerDay(prev);
      setMaxAccessInput(String(prev));
      setSnack({
        open: true,
        message: e?.response?.data?.message || t.saveError,
        severity: 'error',
      });
    } finally {
      setSavingMax(false);
    }
  };

  const applyCaptureIdle = async (nextRaw: number) => {
    const parsed = clampIdleMinutes(nextRaw, captureIdleMinutes);
    if (parsed === captureIdleMinutes) {
      setCaptureIdleInput(String(captureIdleMinutes));
      return;
    }
    const prev = captureIdleMinutes;
    captureIdleDirtyRef.current = false;
    setCaptureIdleMinutes(parsed);
    setCaptureIdleInput(String(parsed));
    setSavingCaptureIdle(true);
    try {
      const res = await api.patch(modeEndpoint, {
        photoboothSimpleCaptureIdleTimeoutMinutes: parsed,
        source: userId ? 'admin' : 'web',
      });
      const mins = clampIdleMinutes(
        res.data?.photoboothSimpleCaptureIdleTimeoutMinutes ?? parsed,
        parsed,
      );
      setCaptureIdleMinutes(mins);
      setCaptureIdleInput(String(mins));
    } catch (e: any) {
      setCaptureIdleMinutes(prev);
      setCaptureIdleInput(String(prev));
      setSnack({
        open: true,
        message: e?.response?.data?.message || t.saveError,
        severity: 'error',
      });
    } finally {
      setSavingCaptureIdle(false);
    }
  };

  const applyPreviewIdle = async (nextRaw: number) => {
    const parsed = clampIdleMinutes(nextRaw, previewIdleMinutes);
    if (parsed === previewIdleMinutes) {
      setPreviewIdleInput(String(previewIdleMinutes));
      return;
    }
    const prev = previewIdleMinutes;
    previewIdleDirtyRef.current = false;
    setPreviewIdleMinutes(parsed);
    setPreviewIdleInput(String(parsed));
    setSavingPreviewIdle(true);
    try {
      const res = await api.patch(modeEndpoint, {
        photoboothSimplePreviewIdleTimeoutMinutes: parsed,
        source: userId ? 'admin' : 'web',
      });
      const mins = clampIdleMinutes(
        res.data?.photoboothSimplePreviewIdleTimeoutMinutes ?? parsed,
        parsed,
      );
      setPreviewIdleMinutes(mins);
      setPreviewIdleInput(String(mins));
    } catch (e: any) {
      setPreviewIdleMinutes(prev);
      setPreviewIdleInput(String(prev));
      setSnack({
        open: true,
        message: e?.response?.data?.message || t.saveError,
        severity: 'error',
      });
    } finally {
      setSavingPreviewIdle(false);
    }
  };

  const emitCaptureCountdown = useCallback((secs: number) => {
    const parsed = clampCaptureCountdown(secs, captureCountdownSeconds);
    const socket = socketRef.current;
    if (!socket?.connected) {
      setSnack({ open: true, message: t.captureCountdownOffline, severity: 'error' });
      return;
    }
    captureCountdownDirtyRef.current = false;
    setCaptureCountdownSeconds(parsed);
    setCaptureCountdownInput(String(parsed));
    socket.emit('device-mgmt-camera-command', {
      machineCode: mc,
      property: 'set-countdown',
      value: parsed,
    });
  }, [captureCountdownSeconds, mc, t.captureCountdownOffline]);

  const handleCopy = async (text: string, target: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(target);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* ignore */
    }
  };

  const renderIdleStepper = ({
    label,
    input,
    setInput,
    dirtyRef,
    savingField,
    savedValue,
    min = IDLE_TIMEOUT_MIN,
    max = IDLE_TIMEOUT_MAX,
    unit,
    onInstantChange,
  }: {
    label: string;
    input: string;
    setInput: (v: string) => void;
    dirtyRef: React.MutableRefObject<boolean>;
    savingField: boolean;
    savedValue: number;
    min?: number;
    max?: number;
    unit?: string;
    onInstantChange: (value: number) => void;
  }) => {
    const clamp = (v: unknown) => {
      const n = Math.floor(Number(v));
      if (!Number.isFinite(n)) return savedValue;
      return Math.max(min, Math.min(max, n));
    };
    const numeric = clamp(input);
    const commit = (next: number) => {
      const value = clamp(next);
      dirtyRef.current = false;
      setInput(String(value));
      if (value !== savedValue) onInstantChange(value);
    };
    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 0.75,
            fontWeight: 700,
            letterSpacing: { xs: '0.01em', sm: '0.04em' },
            textTransform: 'uppercase',
            color: alpha('#0f3460', 0.7),
            fontSize: { xs: '0.58rem', sm: '0.65rem' },
            lineHeight: 1.25,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
        <Box display="flex" gap={{ xs: 0.35, sm: 0.75 }} alignItems="center" sx={{ minWidth: 0 }}>
          <IconButton
            size="small"
            disabled={savingField || numeric <= min}
            onClick={() => commit(numeric - 1)}
            sx={{
              width: { xs: 28, sm: 34 },
              height: { xs: 28, sm: 34 },
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: alpha('#0f3460', 0.06),
              color: '#0f3460',
            }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <TextField
            type="text"
            inputMode="numeric"
            size="small"
            value={input}
            onChange={(e) => {
              dirtyRef.current = true;
              setInput(e.target.value.replace(/[^\d]/g, '').slice(0, 4));
            }}
            onBlur={() => commit(clamp(input))}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              (e.target as HTMLInputElement).blur();
            }}
            inputProps={{
              inputMode: 'numeric',
              style: { textAlign: 'center', fontWeight: 700 },
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: { sm: 72 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: alpha('#0f3460', 0.03),
              },
              '& input': {
                py: { xs: 0.75, sm: 1 },
                fontSize: { xs: '0.9rem', sm: '1rem' },
              },
            }}
            disabled={savingField}
          />
          {!!unit && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, flexShrink: 0, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
            >
              {unit}
            </Typography>
          )}
          <IconButton
            size="small"
            disabled={savingField || numeric >= max}
            onClick={() => commit(numeric + 1)}
            sx={{
              width: { xs: 28, sm: 34 },
              height: { xs: 28, sm: 34 },
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: alpha('#0f3460', 0.06),
              color: '#0f3460',
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  };

  if (!mc) {
    return <Alert severity="warning">{t.missingMachine}</Alert>;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderRadius: 3,
          borderColor: (th) => alpha(th.palette.divider, 0.9),
          boxShadow: '0 4px 16px rgba(15, 52, 96, 0.04)',
        }}
      >
        <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, '&:last-child': { pb: { xs: 1.75, sm: 2.25 } } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              mb: 0.5,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#0f3460' }}>
                {t.titleMachine}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.35 }}>
                {t.descriptionMachine}
              </Typography>
            </Box>
            <Switch
              checked={enabled}
              onChange={onToggle}
              disabled={saving}
              color="primary"
              sx={{ flexShrink: 0 }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              display: 'inline-block',
              mt: 0.25,
              px: 1,
              py: 0.25,
              borderRadius: 999,
              fontWeight: 700,
              fontSize: '0.7rem',
              bgcolor: enabled ? alpha('#059669', 0.1) : alpha('#6b7280', 0.1),
              color: enabled ? '#059669' : '#6b7280',
            }}
          >
            {t.toggleLabel}: {enabled ? t.toggleOnHint : t.toggleOffHint}
          </Typography>

          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              opacity: enabled ? 1 : 0.55,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f3460' }}>
                {t.retakeToggleLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.35 }}>
                {t.retakeToggleHint}
              </Typography>
            </Box>
            <Switch
              checked={retakeEnabled}
              onChange={onToggleRetake}
              disabled={!enabled || savingRetake}
              color="primary"
              sx={{ flexShrink: 0 }}
            />
          </Box>

          <Box
            sx={{
              mt: 2,
              pt: 1.75,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              gap: { xs: 1.25, sm: 1.75 },
              columnGap: { xs: 1, sm: 1.5 },
            }}
          >
            {renderIdleStepper({
              label: t.maxAccessLabel,
              input: maxAccessInput,
              setInput: setMaxAccessInput,
              dirtyRef: maxAccessDirtyRef,
              savingField: savingMax,
              savedValue: maxAccessPerDay,
              min: 0,
              max: 9999,
              onInstantChange: (v) => { void applyMaxAccess(v); },
            })}

            {renderIdleStepper({
              label: t.captureCountdownLabel,
              input: captureCountdownInput,
              setInput: setCaptureCountdownInput,
              dirtyRef: captureCountdownDirtyRef,
              savingField: false,
              savedValue: captureCountdownSeconds,
              min: CAPTURE_COUNTDOWN_MIN,
              max: CAPTURE_COUNTDOWN_MAX,
              unit: t.captureCountdownUnit,
              onInstantChange: emitCaptureCountdown,
            })}

            {renderIdleStepper({
              label: t.captureIdleLabel,
              input: captureIdleInput,
              setInput: setCaptureIdleInput,
              dirtyRef: captureIdleDirtyRef,
              savingField: savingCaptureIdle,
              savedValue: captureIdleMinutes,
              unit: t.idleTimeoutUnit,
              onInstantChange: (v) => { void applyCaptureIdle(v); },
            })}

            {renderIdleStepper({
              label: t.previewIdleLabel,
              input: previewIdleInput,
              setInput: setPreviewIdleInput,
              dirtyRef: previewIdleDirtyRef,
              savingField: savingPreviewIdle,
              savedValue: previewIdleMinutes,
              unit: t.idleTimeoutUnit,
              onInstantChange: (v) => { void applyPreviewIdle(v); },
            })}
          </Box>
        </CardContent>
      </Card>

      {enabled && (
        <Card variant="outlined">
          <CardContent>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="primary"
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}
            >
              {t.qrTitle}
            </Typography>

            <Box display="flex" justifyContent="center" my={1.5}>
              <Box
                role="button"
                tabIndex={0}
                aria-label={t.openLink}
                onClick={() => window.open(publicUrl, '_blank')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    window.open(publicUrl, '_blank');
                  }
                }}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  bgcolor: 'white',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  lineHeight: 0,
                  cursor: 'pointer',
                  transition: 'opacity 0.15s ease, box-shadow 0.15s ease',
                  '&:hover': {
                    opacity: 0.92,
                    boxShadow: (th) => `0 4px 14px ${alpha(th.palette.primary.main, 0.2)}`,
                  },
                  '&:active': { opacity: 0.85 },
                  '& svg': { display: 'block', maxWidth: '100%', height: 'auto' },
                }}
              >
                <QRCodeSVG value={publicUrl} size={isMobile ? 196 : 220} level="M" includeMargin />
              </Box>
            </Box>

            {isClaimed ? (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {t.sessionInUse}
              </Alert>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.75,
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                  }}
                >
                  {t.accessCodeTitle}
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 0.65,
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: (th) => alpha(th.palette.primary.main, 0.35),
                    bgcolor: (th) => alpha(th.palette.primary.main, 0.08),
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 800,
                      fontSize: '1.35rem',
                      letterSpacing: '0.12em',
                      color: 'primary.dark',
                      lineHeight: 1,
                    }}
                  >
                    {accessCode || '---'}
                  </Typography>
                  <IconButton
                    size="small"
                    disabled={!accessCode}
                    onClick={() => accessCode && handleCopy(accessCode, 'code')}
                    sx={{ color: 'primary.main', p: 0.5 }}
                  >
                    {copied === 'code' ? (
                      <CheckCircleIcon fontSize="small" color="success" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </Box>
            )}

            <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                startIcon={copied === 'link' ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                onClick={() => handleCopy(publicUrl, 'link')}
              >
                {t.copyLink}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {enabled && (
        <Box sx={{ mt: 1.5 }}>
          <StaffUnlimitedQrCard
            machineCode={mc}
            purpose="photobooth-simple"
            qrSize={isMobile ? 196 : 220}
          />
        </Box>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PhotoboothSimpleMode;
